import React from 'react';
import { Box, Container, Typography, Grid, Chip, Divider } from '@mui/material';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import ShieldIcon from '@mui/icons-material/Shield';
import BoltIcon from '@mui/icons-material/Bolt';
import GroupsIcon from '@mui/icons-material/Groups';

const PRIMARY = '#FF7518';
const DARK = '#4a2d08';
const DARK_BG = '#0f0f0f';

const values = [
  {
    icon: <ShieldIcon sx={{ fontSize: 32, color: PRIMARY }} />,
    title: 'Security First',
    description:
      'We build with the assumption that healthcare data is sacred. Every feature, rule, and integration is designed to protect PHI and stay ahead of emerging threats.',
  },
  {
    icon: <BoltIcon sx={{ fontSize: 32, color: PRIMARY }} />,
    title: 'Developer Native',
    description:
      'Compliance should live in the same workflow as your code. BaaStream runs in your environment, integrates into CI/CD, and gives you results in seconds — not weeks.',
  },
  {
    icon: <GroupsIcon sx={{ fontSize: 32, color: PRIMARY }} />,
    title: 'Healthcare Focused',
    description:
      'We built BaaStream for healthcare engineering teams specifically. Our rule library is curated by compliance experts with deep domain knowledge in HIPAA, FHIR, and HITRUST.',
  },
];

export function AboutUs() {
  return (
    <Box sx={{ bgcolor: '#fff', minHeight: '100vh' }}>
      {/* Minimal Nav */}
      <Box sx={{ bgcolor: DARK_BG, py: 2, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Container maxWidth="lg">
          <Box
            component="a"
            href="/"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, textDecoration: 'none' }}
          >
            <HealthAndSafetyIcon sx={{ color: PRIMARY, fontSize: 26 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>
              BaaStream
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Hero */}
      <Box sx={{ bgcolor: DARK_BG, pb: { xs: 8, md: 10 }, pt: { xs: 6, md: 8 } }}>
        <Container maxWidth="md">
          <Chip
            label="About Us"
            size="small"
            sx={{ bgcolor: 'rgba(255,117,24,0.15)', color: PRIMARY, fontWeight: 600, mb: 3 }}
          />
          <Typography
            variant="h1"
            sx={{ fontSize: { xs: '2rem', md: '2.8rem' }, fontWeight: 800, color: '#fff', lineHeight: 1.2, mb: 3 }}
          >
            Built by engineers who lived the{' '}
            <Box component="span" sx={{ color: PRIMARY }}>
              healthcare compliance pain
            </Box>
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '1.1rem', lineHeight: 1.8, maxWidth: 620 }}>
            BaaStream is a product of Monks Do Work — built to solve a problem every healthcare
            engineering team knows: compliance tooling designed for auditors, not developers.
            We're changing that.
          </Typography>
        </Container>
      </Box>

      {/* Company */}
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 10 } }}>

        {/* Parent Company */}
        <Box
          sx={{
            p: { xs: 3, md: 4 },
            bgcolor: 'rgba(255,117,24,0.04)',
            border: `1px solid rgba(255,117,24,0.2)`,
            borderRadius: 2,
            mb: 6,
          }}
        >
          <Typography variant="overline" sx={{ color: PRIMARY, fontWeight: 700, letterSpacing: 1.5, display: 'block', mb: 1 }}>
            Parent Company
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 800, color: DARK, mb: 2, fontSize: { xs: '1.4rem', md: '1.7rem' } }}>
            Monks Do Work
          </Typography>
          <Typography variant="body1" sx={{ color: '#555', lineHeight: 1.9 }}>
            Monks Do Work is a technology company that builds focused, developer-first software
            products. BaaStream is our flagship product — a CLI-native healthcare compliance
            scanner purpose-built for DevOps and DevSecOps teams operating in regulated healthcare
            environments. All BaaStream services, subscriptions, and legal obligations are operated
            by Monks Do Work.
          </Typography>
        </Box>

        <Typography variant="h2" sx={{ fontWeight: 800, color: DARK, fontSize: { xs: '1.7rem', md: '2.2rem' }, mb: 3 }}>
          Our Mission
        </Typography>
        <Typography variant="body1" sx={{ color: '#555', lineHeight: 1.9, fontSize: '1.05rem', mb: 4 }}>
          Healthcare engineering teams deserve compliance tools that work the same way their code does —
          fast, automated, and integrated into every step of the development lifecycle. BaaStream gives
          developers a CLI-native compliance scanner that catches HIPAA, FHIR, HITRUST, SOC 2, and GDPR
          violations before they reach production.
        </Typography>
        <Typography variant="body1" sx={{ color: '#555', lineHeight: 1.9, fontSize: '1.05rem' }}>
          We believe that security and compliance shouldn't slow down healthcare innovation. With 245+ curated
          rules, sub-5-second scan times, and audit-ready exports, BaaStream helps teams ship faster and
          safer — without sacrificing their compliance posture.
        </Typography>

        <Divider sx={{ my: 6 }} />

        <Typography variant="h2" sx={{ fontWeight: 800, color: DARK, fontSize: { xs: '1.7rem', md: '2.2rem' }, mb: 5 }}>
          Our Values
        </Typography>
        <Grid container spacing={4}>
          {values.map((v) => (
            <Grid size={{ xs: 12, md: 4 }} key={v.title}>
              <Box sx={{ mb: 2 }}>{v.icon}</Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: DARK, mb: 1.5 }}>
                {v.title}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.8 }}>
                {v.description}
              </Typography>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 6 }} />

        <Typography variant="h2" sx={{ fontWeight: 800, color: DARK, fontSize: { xs: '1.7rem', md: '2.2rem' }, mb: 3 }}>
          What We Build
        </Typography>
        <Typography variant="body1" sx={{ color: '#555', lineHeight: 1.9, fontSize: '1.05rem', mb: 3 }}>
          BaaStream is a CLI-based compliance scanner purpose-built for healthcare DevOps. It scans
          configuration files, FHIR resources, infrastructure code, and log files against 245+ rules
          spanning major compliance frameworks and cloud providers — in under 5 seconds.
        </Typography>
        <Box component="ul" sx={{ color: '#555', pl: 3, '& li': { mb: 1.5, lineHeight: 1.8 } }}>
          <li>245+ rules covering HIPAA, FHIR R4/R5, HITRUST CSF, SOC 2, GDPR, and ISO 27001</li>
          <li>Native support for AWS HealthLake, Azure Health Data Services, and GCP Healthcare API</li>
          <li>GitHub Actions, Jenkins, and Terraform integrations for CI/CD automation</li>
          <li>Audit-ready PDF and Excel exports mapped to HIPAA §164.308 and §164.312</li>
          <li>Team dashboard with compliance trend analytics and organization-wide visibility</li>
        </Box>

        <Divider sx={{ my: 6 }} />

        <Typography variant="h2" sx={{ fontWeight: 800, color: DARK, fontSize: { xs: '1.7rem', md: '2.2rem' }, mb: 3 }}>
          Get in Touch
        </Typography>
        <Typography variant="body1" sx={{ color: '#555', lineHeight: 1.9, fontSize: '1.05rem' }}>
          We'd love to hear from healthcare engineering teams, compliance officers, and anyone building
          in the healthcare technology space. Reach us at{' '}
          <Box
            component="a"
            href="mailto:hello@baastream.com"
            sx={{ color: PRIMARY, textDecoration: 'none', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
          >
            hello@baastream.com
          </Box>{' '}
          or visit our{' '}
          <Box
            component="a"
            href="/contact"
            sx={{ color: PRIMARY, textDecoration: 'none', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
          >
            Contact page
          </Box>
          .
        </Typography>
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: DARK_BG, py: 4, mt: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>
              © 2025 Monks Do Work. All rights reserved. BaaStream is a product of Monks Do Work.
            </Typography>
            <Box component="a" href="/" sx={{ color: PRIMARY, fontSize: '0.8rem', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
              ← Back to Home
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
